import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChefHat, Plus, Trash2, Eraser } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { g, p, del, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const GIRI = [
  { course: 1, label: "Per primo" },
  { course: 2, label: "Nel mezzo" },
  { course: 3, label: "Per ultimo" },
];

export default function PiattiGiorno() {
  const { departments, user } = useApp();
  const reparti = departments?.length ? departments : ["Cucina", "Pizzeria", "Bar"];
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", price: "", department: reparti[0], course: 2 });

  const load = async () => setRows(await g("/piatti-giorno"));
  useEffect(() => {
    load();
  }, []);

  const aggiungi = async () => {
    try {
      const prezzo = Number(String(form.price).replace(",", ".")) || 0;
      await p("/piatti-giorno", { ...form, price: prezzo, by: user?.name });
      setForm({ name: "", price: "", department: form.department, course: form.course });
      toast.success("Aggiunto ai piatti del giorno");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="piatti-giorno-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">Lavagna di oggi</p>
        <h1 className="mt-1 text-2xl font-bold sm:text-3xl">Piatti del giorno</h1>
        <p className="mt-1 text-sm text-slate-500">
          Quello che scrivi qui finisce subito nel menu, nel gruppo «Piatti del giorno»: si ordina
          come tutto il resto, con la quantità, il giro e le note. Puoi metterci anche da bere.
        </p>
      </div>

      <Card className="border-2 border-slate-900 p-5">
        <div className="grid gap-3 sm:grid-cols-[2fr_auto_auto_auto]">
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && aggiungi()}
            placeholder="Es. Paccheri con lo scorfano"
            data-testid="pg-name"
          />
          <Input
            value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
            inputMode="decimal"
            className="font-num sm:w-28"
            placeholder="€"
            data-testid="pg-price"
          />
          <div className="flex gap-1">
            {reparti.map((r) => (
              <Button
                key={r}
                type="button"
                size="sm"
                variant={form.department === r ? "default" : "outline"}
                onClick={() => setForm({ ...form, department: r })}
                data-testid={`pg-dept-${r}`}
              >
                {r}
              </Button>
            ))}
          </div>
          <Button onClick={aggiungi} data-testid="pg-add">
            <Plus className="mr-1.5 h-4 w-4" /> Aggiungi
          </Button>
        </div>
        <div className="mt-3 flex flex-wrap gap-1">
          {GIRI.map((x) => (
            <Button
              key={x.course}
              type="button"
              size="sm"
              variant={form.course === x.course ? "default" : "outline"}
              onClick={() => setForm({ ...form, course: x.course })}
              data-testid={`pg-course-${x.course}`}
            >
              {x.label}
            </Button>
          ))}
        </div>
      </Card>

      <Card className="p-5" data-testid="pg-list">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">
            Oggi in lavagna · {rows.length}
          </p>
          {rows.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              data-testid="pg-clear"
              onClick={async () => {
                if (!window.confirm(`Tolgo tutti i ${rows.length} piatti del giorno?`)) return;
                try {
                  await del("/piatti-giorno");
                  toast.success("Lavagna pulita");
                  load();
                } catch (e) {
                  toast.error(errMsg(e));
                }
              }}
            >
              <Eraser className="mr-1.5 h-4 w-4" /> Svuota tutto
            </Button>
          )}
        </div>
        {rows.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">
            Ancora niente. Scrivi il primo piatto qui sopra: lo vedranno subito tutti i camerieri.
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-100">
            {rows.map((r) => (
              <li key={r.id} className="flex items-center gap-3 py-2.5" data-testid={`pg-row-${r.id}`}>
                <ChefHat className="h-4 w-4 shrink-0 text-slate-400" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold">{r.name}</span>
                  <span className="text-xs text-slate-500">
                    {r.department} · {GIRI.find((x) => x.course === r.course)?.label || "Nel mezzo"}
                  </span>
                </span>
                <span className="font-num shrink-0 font-semibold">{r.price.toFixed(2)} €</span>
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid={`pg-del-${r.id}`}
                  onClick={async () => {
                    try {
                      await del(`/piatti-giorno/${r.id}`);
                      load();
                    } catch (e) {
                      toast.error(errMsg(e));
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4 text-rose-600" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
